// The build queue's dependency rules (R387, R1201): which job cannot start
// before which, the first pair a queue order breaks, and the order a move
// of `n` places would produce under MoveJob's clamps. Pure functions of a
// job list. The queue rows below read them to kill a button before the
// server refuses the same order, so the player reads the reason on the
// button instead of meeting it as a refusal.
//
// The queue strip of the dock is here too, as pure markup: its one line
// and, opened, one row per job. `ui/overlay.ts` writes it. A desktop
// strip has three states (R2740): minimized to its line, SMALL with one
// line per job, and LARGE with the whole rows.

import { type Job, JobKind } from '../../gen/proto/aquaphobia';
import { overlay as S } from '../../gen/strings';
import { type GameState, dur } from '../../state';
import { arrowFace, arrowGlyph, minGlyph } from '../arrowpress';
import { minIcon } from '../chart/frame';
import { armFaces } from '../choice/choice';
import { esc } from '../esc';
import { withResIcons } from '../resicon';

/**
 * Whether `succ` cannot start before `pred` has finished — the client's
 * mirror of `game.dependsOn` (R387). Two levels of the same building are
 * the one chain the queue can hold: Sonar Station 3 is enqueued as the
 * successor of the Sonar Station 2 ahead of it. Every other gate reads a
 * COMPLETED figure, so no second chain forms.
 *
 * This is a courtesy, not the rule: the server refuses the same two cases
 * whatever the buttons look like.
 */
export function dependsOn(succ: Job, pred: Job): boolean {
  return succ.kind === JobKind.JOB_KIND_BUILDING
    && pred.kind === JobKind.JOB_KIND_BUILDING
    && succ.target === pred.target && succ.level > pred.level;
}

/** The first pair in `q` where a successor sits ahead of its predecessor. */
export function brokenChain(q: Job[]): [Job, Job] | null {
  for (let i = 0; i < q.length; i++) {
    for (let k = i + 1; k < q.length; k++) {
      if (dependsOn(q[i], q[k])) return [q[i], q[k]];
    }
  }
  return null;
}

/**
 * The order a `qmove` of `n` places would produce, with MoveJob's clamps.
 * A jump toward the front (n > 1) stops just behind the job's last queued
 * predecessor, and a jump toward the back (n < -1) stops just ahead of its
 * first queued successor (R1201). A jump that gains no place returns the
 * order that breaks the chain, so `brokenChain` kills the button with the
 * same pair the server's refusal names.
 */
export function movedOrder(q: Job[], at: number, n: number): Job[] {
  let to = at - n;
  if (to < 0) to = 0;
  if (to >= q.length) to = q.length - 1;
  if (n > 1) {
    let last = -1;
    q.forEach((p, k) => { if (k !== at && dependsOn(q[at], p)) last = k; });
    if (last >= 0 && to <= last) {
      to = last + 1;
      // No place to gain: the step order breaks the chain, so the caller
      // reads the same refusal the server sends.
      if (to >= at) to = last;
    }
  }
  if (n < -1) {
    // The mirror toward the back: stop just ahead of the first queued
    // successor, which sits at s-1 once the job is lifted out.
    const first = q.findIndex((s, k) => k !== at && dependsOn(s, q[at]));
    if (first >= 0 && to >= first) {
      to = first - 1;
      if (to <= at) to = first;
    }
  }
  const next = q.slice();
  next.splice(at, 1);
  next.splice(to, 0, q[at]);
  return next;
}

/** The desktop form of the strip (R2740): the size of the open strip.
 *  A telephone passes none and keeps the caret of R1856. */
interface QueueDesk {
  /** LARGE: the whole rows with their presses. SMALL: one line per job. */
  large: boolean;
}

/**
 * The end of the queue line. A telephone draws the caret (▴ closed, ▾
 * open). A desktop draws the restore glyph of the minimized chat box and
 * legend card on the minimized line (the chevron of `minIcon`), and on
 * the open strip the size press and the minimize press of the chat box
 * head: ▴ makes the strip LARGE, ▾ makes it SMALL, – minimizes it (R2740).
 */
function lineEnd(open: boolean, desk?: QueueDesk): string {
  if (!desk) return `<span class="qcaret">${arrowFace(open ? 'down' : 'up')}</span>`;
  if (!open) {
    return `<span class="qcaret" title="${esc(S.queue_restore_title)}">`
      + `<span class="sqp">${minIcon(true)}</span></span>`;
  }
  const size = desk.large ? S.queue_small_title : S.queue_big_title;
  return '<span class="qcaret">'
    + `<button class="qsize sqp" data-act="queuesize" title="${esc(size)}" aria-label="${esc(size)}"`
    + ` aria-pressed="${desk.large}">${arrowGlyph(desk.large ? 'down' : 'up')}</button>`
    + `<button class="qmin sqp" data-act="queuemin" title="${esc(S.queue_min_title)}"`
    + ` aria-label="${esc(S.queue_min_title)}">${minGlyph()}</button></span>`;
}

/**
 * The queue strip (§12.0): one line, or opened into the whole queue. The
 * line states the slots and the wait, then the first three jobs with the
 * clock of each one that runs, and the end of the line (`lineEnd`).
 * `armed` reads the overlay's arm gate (R696). `desk` is the desktop form
 * (R2740); a SMALL strip writes one line per job.
 */
export function queueStripHtml(
  s: GameState, open: boolean, armed: (key: string) => boolean, desk?: QueueDesk,
): string {
  const e = s.empire;
  if (!e) return '';
  const active = e.queue.filter((j) => j.active);
  // Both halves are durations. The left half, `queueWaitSec`, is the wait
  // for a free build slot (R726), which is the figure the ceiling bites
  // on, so the reading is true at `{max}`. It is not the drain time and
  // not the sum of the work. The server owns the schedule, the client
  // only prints it.
  const budget = e.queueMaxSec > 0
    ? S.queue_budget({ used: dur(e.queueWaitSec), max: dur(e.queueMaxSec) }) : '';
  // An empty queue names its state in place of the slots and the wait,
  // which read `0/1 · −/2d 0h` to a new commander (R2135).
  const head = e.queue.length === 0
    ? S.queue_head_idle : S.queue_head({ active: active.length, slots: e.slots, budget });
  let html = `<div class="qline"><span class="qhead">${head}</span>`;
  if (e.queue.length === 0) {
    html += `<span class="muted">${withResIcons(S.queue_idle({
      mint: s.rules.idleCreditsPerHour,
    }))}</span>`;
  } else {
    for (const j of e.queue.slice(0, open ? 0 : 3)) {
      // The ETA, never the work figure: remaining_ms is work-milliseconds
      // at 1x and printing it as a duration is wrong at any Works Yard
      // level above 1. state.jobEtaSec counts the honest figure down on
      // the world clock between Empire frames.
      html += `<span class="qjob${j.active ? '' : ' waiting'}">${esc(j.label)}`
        + (j.active ? ` <span class="muted">${dur(s.jobEtaSec(j))}</span>` : '') + `</span>`;
    }
    if (!open && e.queue.length > 3) html += `<span class="muted">+${e.queue.length - 3}</span>`;
  }
  html += `${lineEnd(open, desk)}</div>`;
  return open ? html + queueRowsHtml(s, e.queue, armed, desk !== undefined && !desk.large) : html;
}

/** The rows of the open queue strip: the clock, the bar and the presses
 *  of each job, or the idle line of an empty queue. A `compact` row is
 *  the row of the SMALL desktop strip (R2740): the name, the clock and
 *  the bar on one line, with no presses. */
export function queueRowsHtml(
  s: GameState, queue: Job[], armed: (key: string) => boolean, compact = false,
): string {
  if (queue.length === 0) {
    return `<div class="empty">${withResIcons(S.queue_idle_full({
      mint: s.rules.idleCreditsPerHour,
    }))}</div>`;
  }
  return queue.map((j, at) => {
    // Two different quantities, and the row shows one of each. The clock
    // is `etaSec`, world game-seconds, which is the only honest answer to
    // "when does it land". The bar is *work*: remaining_ms and total_ms
    // are work-milliseconds at 1x speed, so their ratio is progress and
    // never a duration. Both count between Empire frames off the same
    // anchor, because a draining job is not a change the server sends.
    const live = s.jobLive(j);
    const pct = j.totalMs > 0 ? 100 * (1 - live.remainingMs / j.totalMs) : 100;
    const head = `<div class="head"><span class="name">${esc(j.label)}</span>`
      // A job that holds no slot has no ETA to give — the server sends 0
      // for it — so the row says it is waiting rather than printing a
      // dash where a clock belongs.
      + `<span class="muted">${j.active ? dur(live.etaSec) : S.queue_waiting}</span></div>`
      + `<div class="bar"><span style="width:${pct.toFixed(1)}%"></span></div>`;
    if (compact) return `<div class="job compact${j.active ? '' : ' idle'}">${head}</div>`;
    // A move that would put a successor in front of its predecessor is
    // dead, and its tooltip says which pair it broke (R387). The player
    // reads the reason on the button instead of meeting it as a refusal.
    // The four buttons always stand, so each keeps its place from row to
    // row for a batch of moves. An edge has no move to make: `edge` greys it.
    const mv = (n: number, label: string, edge: boolean) => {
      const bad = edge ? null : brokenChain(movedOrder(queue, at, n));
      const title = bad ? S.queue_move_blocked({ job: bad[0].label, pred: bad[1].label }) : label;
      return `<button data-act="qmove" data-id="${j.id}" data-n="${n}"`
        + ` title="${esc(title)}"${edge || bad ? ' disabled' : ''}>`;
    };
    // The same rule for the cancel: a predecessor a queued successor still
    // needs must stay.
    const heir = queue.find((x) => x !== j && dependsOn(x, j));
    const cancelWhy = heir
      ? S.queue_cancel_blocked({ job: j.label, succ: heir.label }) : '';
    // And the same rule for the rush: a rush past a queued predecessor
    // would write the higher level first and then lose it (R417).
    const pred = queue.find((x) => x !== j && dependsOn(j, x));
    const rushWhy = pred
      ? S.queue_rush_blocked({ job: j.label, pred: pred.label }) : '';
    const cancelArmed = armed(`cancel:${j.id}`);
    return `<div class="job${j.active ? '' : ' idle'}">${head}`
      + `<div class="acts">`
      + `${mv(queue.length, S.queue_to_top, at === 0)}▲▲</button>`
      + `${mv(1, S.queue_earlier, at === 0)}▲</button>`
      + `${mv(-1, S.queue_later, at === queue.length - 1)}▼</button>`
      + `${mv(-queue.length, S.queue_to_bottom, at === queue.length - 1)}▼▼</button>`
      + `<button data-act="rush" data-id="${j.id}"`
      + (pred ? ` title="${esc(rushWhy)}" disabled` : '')
      + `>${withResIcons(S.queue_rush({ cost: j.rushCost }))}</button>`
      // A cancel that throws work away asks for a second press (R696).
      // A job that has not started yet loses nothing, so it keeps its
      // one press.
      + `<button class="danger${cancelArmed ? ' armed' : ''}"`
      + ` data-act="cancel" data-id="${j.id}"`
      + (j.active && pct > 0 && !heir ? ' data-arm="1"' : '')
      + (heir ? ` title="${esc(cancelWhy)}" disabled` : '')
      + `>${armFaces(S.queue_cancel, S.queue_cancel_armed, cancelArmed)}</button>`
      + `</div></div>`;
  }).join('');
}
